import { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Publish as PublishIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Tariff, TemplateVersion, UIVariantType, UI_VARIANTS, UI_VARIANT_LABELS, uiVariantToDbKey, PrintOffsets, PrintOffset } from '../types';
import { tariffApi, templateApi, publishingApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import ToothSpinner from './ToothSpinner';

interface VariantVersions {
  SAME: TemplateVersion[];
  DIFFERENT: TemplateVersion[];
  KIDS: TemplateVersion[];
}

function TariffDetail() {
  const { tariffId } = useParams<{ tariffId: string }>();
  const navigate = useNavigate();
  const { userEmail } = useAuth();

  const [tariff, setTariff] = useState<Tariff | null>(null);
  const [variantVersions, setVariantVersions] = useState<VariantVersions>({
    SAME: [],
    DIFFERENT: [],
    KIDS: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState<Record<UIVariantType, number | null>>({
    SAME: null,
    DIFFERENT: null,
    KIDS: null,
  });
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [savingProvider, setSavingProvider] = useState(false);
  const [expandedVariant, setExpandedVariant] = useState<UIVariantType | null>(null);
  const [printOffsets, setPrintOffsets] = useState<PrintOffsets | null>(null);
  const [savingOffsets, setSavingOffsets] = useState(false);
  const [offsetsExpanded, setOffsetsExpanded] = useState(false);

  useEffect(() => {
    if (tariffId) {
      fetchTariffDetails();
    }
  }, [tariffId]);

  const fetchTariffDetails = async () => {
    if (!tariffId) return;
    try {
      setLoading(true);
      setError(null);
      const tariffData = await tariffApi.getTariff(parseInt(tariffId));
      setTariff(tariffData.tariff);
      const offsets = tariffData.tariff.PrintOffsets ?? null;
      setPrintOffsets(offsets);
      setOffsetsExpanded(offsets !== null);

      const versionsData: VariantVersions = { SAME: [], DIFFERENT: [], KIDS: [] };
      await Promise.all(
        UI_VARIANTS.map(async (uiVariant) => {
          try {
            const dbKey = uiVariantToDbKey(uiVariant);
            const data = await templateApi.getTemplatesByTariffAndVariant(parseInt(tariffId), dbKey);
            versionsData[uiVariant] = data.templates.sort((a, b) => b.version - a.version);
          } catch (err) {
            console.error(`Error fetching versions for variant ${uiVariant}:`, err);
          }
        })
      );
      setVariantVersions(versionsData);

      const preSelected: Record<UIVariantType, number | null> = { SAME: null, DIFFERENT: null, KIDS: null };
      UI_VARIANTS.forEach((uiVariant) => {
        const publishedVersion = versionsData[uiVariant].find(v => v.status === 'published');
        if (publishedVersion) preSelected[uiVariant] = publishedVersion.version;
      });
      setSelectedVersions(preSelected);
    } catch (err) {
      setError('Failed to load tariff details');
      console.error('Error fetching tariff details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'default' => {
    switch (status) {
      case 'published': return 'success';
      case 'draft': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleOpenBulkDialog = () => { setBulkDialogOpen(true); setPublishError(null); };
  const handleCloseBulkDialog = () => { setBulkDialogOpen(false); setPublishError(null); };

  const handleVersionSelect = (uiVariant: UIVariantType, version: number | null) => {
    setSelectedVersions(prev => ({ ...prev, [uiVariant]: version }));
  };

  const handleBulkPublish = async () => {
    if (!tariffId) return;
    if (!userEmail) { setPublishError('User email not available. Please log in again.'); return; }
    const allSelected = UI_VARIANTS.every(uiVariant => selectedVersions[uiVariant] !== null);
    if (!allSelected) { setPublishError('Please select a version for all three variants'); return; }

    try {
      setPublishing(true);
      setPublishError(null);
      // Send the 3 UI variants: SAME, DIFFERENT, KIDS
      // Backend handles mapping KIDS → DIFFERENT_EMAIL_KIDS + DIFFERENT_POST_KIDS on the tariff
      const versionsToPublish: Record<string, number> = {
        SAME: selectedVersions.SAME!,
        DIFFERENT: selectedVersions.DIFFERENT!,
        KIDS: selectedVersions.KIDS!,
      };
      await publishingApi.bulkPublish(parseInt(tariffId), versionsToPublish as any, userEmail);
      await fetchTariffDetails();
      handleCloseBulkDialog();
    } catch (err) {
      setPublishError('Failed to publish templates. Please try again.');
      console.error('Error publishing templates:', err);
    } finally {
      setPublishing(false);
    }
  };

  const handleChangeProvider = async (newProvider: string) => {
    if (!tariffId || !tariff) return;
    try {
      setSavingProvider(true);
      await tariffApi.updateTariff(parseInt(tariffId), { automationProvider: newProvider });
      setTariff({ ...tariff, automationProvider: newProvider });
    } catch (err) {
      console.error('Error updating automation provider:', err);
      setError('Failed to update automation provider');
    } finally {
      setSavingProvider(false);
    }
  };

  const DEFAULT_PRINT_OFFSETS: PrintOffsets = {
    dateTab: { left: -35, top: -5 },
    radioTabWimpel: { left: -35, top: 0 },
    radioTabX: { left: 5, top: 0 },
    replacedReplacementText: { left: 5, top: 0 },
    replacementText: { left: 35, top: 0 },
    signTab: { left: -25, top: 0 },
    txtTab: { left: -35, top: 0 },
    locked: { left: 5, top: 0 },
  };

  const OFFSET_LABELS: Record<keyof PrintOffsets, string> = {
    dateTab: 'Date Tab',
    radioTabWimpel: 'Radio Tab Wimpel',
    radioTabX: 'Radio Tab X',
    replacedReplacementText: 'Replaced Replacement Text',
    replacementText: 'Replacement Text',
    signTab: 'Sign Tab',
    txtTab: 'Text Tab',
    locked: 'Locked',
  };

  const handleOffsetChange = (key: keyof PrintOffsets, axis: keyof PrintOffset, value: string) => {
    if (!printOffsets) return;
    const num = value === '' || value === '-' ? 0 : parseInt(value, 10);
    setPrintOffsets({ ...printOffsets, [key]: { ...printOffsets[key], [axis]: isNaN(num) ? 0 : num } });
  };

  const handleSaveOffsets = async () => {
    if (!tariffId || !printOffsets) return;
    try {
      setSavingOffsets(true);
      await tariffApi.updateTariff(parseInt(tariffId), { PrintOffsets: printOffsets });
      setTariff(prev => prev ? { ...prev, PrintOffsets: printOffsets } : prev);
    } catch (err) {
      console.error('Error saving print offsets:', err);
      setError('Failed to save print offsets');
    } finally {
      setSavingOffsets(false);
    }
  };

  const handleRemoveOffsets = async () => {
    if (!tariffId) return;
    try {
      setSavingOffsets(true);
      await tariffApi.updateTariff(parseInt(tariffId), { PrintOffsets: null });
      setPrintOffsets(null);
      setOffsetsExpanded(false);
      setTariff(prev => prev ? { ...prev, PrintOffsets: undefined } : prev);
    } catch (err) {
      console.error('Error removing print offsets:', err);
      setError('Failed to remove print offsets');
    } finally {
      setSavingOffsets(false);
    }
  };

  const handleAddOffsets = () => {
    setPrintOffsets(DEFAULT_PRINT_OFFSETS);
    setOffsetsExpanded(true);
  };

  const getPublishedVersion = (uiVariant: UIVariantType): TemplateVersion | undefined => {
    return variantVersions[uiVariant].find(v => v.status === 'published');
  };

  const getLatestDraft = (uiVariant: UIVariantType): TemplateVersion | undefined => {
    return variantVersions[uiVariant].find(v => v.status === 'draft');
  };

  const renderVariantCard = (uiVariant: UIVariantType) => {
    const versions = variantVersions[uiVariant];
    const dbKey = uiVariantToDbKey(uiVariant);
    const currentTemplateId = tariff?.documensoTemplates?.[dbKey];
    const published = getPublishedVersion(uiVariant);
    const latestDraft = getLatestDraft(uiVariant);
    const isExpanded = expandedVariant === uiVariant;

    const variantColors: Record<UIVariantType, string> = {
      SAME: '#1976d2',
      DIFFERENT: '#7b1fa2',
      KIDS: '#ed6c02',
    };
    const accentColor = variantColors[uiVariant];

    return (
      <Card
        key={uiVariant}
        sx={{
          flex: 1,
          minWidth: 280,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          transition: 'box-shadow 0.2s, border-color 0.2s',
          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderColor: accentColor },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Color accent bar */}
        <Box sx={{ height: 4, bgcolor: accentColor, borderRadius: '12px 12px 0 0' }} />

        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                {UI_VARIANT_LABELS[uiVariant]}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {currentTemplateId ? `${currentTemplateId.substring(0, 12)}…` : 'No template'}
              </Typography>
            </Box>
            <Tooltip title="Open Editor">
              <IconButton
                size="small"
                onClick={() => navigate(`/editor/${tariffId}/${uiVariant}`)}
                sx={{ bgcolor: `${accentColor}10`, color: accentColor, '&:hover': { bgcolor: `${accentColor}20` } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Status badges */}
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            {published ? (
              <Chip
                label={`v${published.version} published`}
                color="success"
                size="small"
                variant="outlined"
                sx={{ borderRadius: 2, fontWeight: 500 }}
              />
            ) : (
              <Chip label="Not published" size="small" variant="outlined" sx={{ borderRadius: 2 }} />
            )}
            {latestDraft && (
              <Chip
                label={`v${latestDraft.version} draft`}
                color="warning"
                size="small"
                variant="outlined"
                sx={{ borderRadius: 2, fontWeight: 500 }}
              />
            )}
          </Stack>

          {/* Stats row */}
          <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Versions</Typography>
              <Typography variant="h6" fontWeight={600}>{versions.length}</Typography>
            </Box>
            {published && (
              <Box>
                <Typography variant="caption" color="text.secondary">Published</Typography>
                <Typography variant="body2" fontWeight={500}>{formatDate(published.publishedAt || published.createdAt)}</Typography>
              </Box>
            )}
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Version history toggle */}
          {versions.length > 0 && (
            <>
              <Button
                size="small"
                onClick={() => setExpandedVariant(isExpanded ? null : uiVariant)}
                endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', color: 'text.secondary', px: 0 }}
              >
                {isExpanded ? 'Hide' : 'Show'} version history
              </Button>
              <Collapse in={isExpanded}>
                <Stack spacing={0} sx={{ mt: 1 }}>
                  {versions.map((v) => (
                    <Box
                      key={v.version}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 1.5,
                        borderRadius: 1.5,
                        bgcolor: v.status === 'published' ? 'success.50' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 32 }}>
                          v{v.version}
                        </Typography>
                        <Chip label={v.status} color={getStatusColor(v.status)} size="small" sx={{ borderRadius: 1.5, height: 22, fontSize: '0.7rem' }} />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(v.createdAt)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Collapse>
            </>
          )}
          {versions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No versions yet
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <ToothSpinner />
      </Box>
    );
  }

  if (error || !tariff) {
    return <Alert severity="error">{error || 'Tariff not found'}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
        >
          Tariffs
        </Link>
        <Typography variant="body2" color="text.primary">{tariff.TARIF}</Typography>
      </Breadcrumbs>

      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            {tariff.VERSICHERUNG}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              ID {tariff.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">·</Typography>
            <Typography variant="body2" color="text.secondary">
              {tariff.TARIF}
            </Typography>
          </Stack>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={tariff.automationProvider || ''}
              onChange={(e) => handleChangeProvider(e.target.value)}
              disabled={savingProvider}
              sx={{ borderRadius: 2, fontWeight: 600, '& .MuiSelect-select': { py: 1 } }}
            >
              <MenuItem value="DocuSign">DocuSign</MenuItem>
              <MenuItem value="Documenso">Documenso</MenuItem>
            </Select>
          </FormControl>
          {savingProvider && <CircularProgress size={20} />}
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={handleOpenBulkDialog}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            Bulk Activate
          </Button>
        </Stack>
      </Box>

      {/* Variant cards grid */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 4 }}>
        {UI_VARIANTS.map(renderVariantCard)}
      </Box>

      {/* Print Offsets Section */}
      {printOffsets ? (
        <Card sx={{ mb: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, cursor: 'pointer' }}
            onClick={() => setOffsetsExpanded(!offsetsExpanded)}
          >
            <Typography variant="subtitle1" fontWeight={600}>Print Offsets</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {savingOffsets && <CircularProgress size={18} />}
              <IconButton size="small">{offsetsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
            </Stack>
          </Box>
          <Collapse in={offsetsExpanded}>
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 2 }}>
                {(Object.keys(OFFSET_LABELS) as (keyof PrintOffsets)[]).map((key) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ minWidth: 180, fontWeight: 500 }}>{OFFSET_LABELS[key]}</Typography>
                    <TextField
                      label="Left"
                      type="number"
                      size="small"
                      value={printOffsets[key].left}
                      onChange={(e) => handleOffsetChange(key, 'left', e.target.value)}
                      sx={{ width: 80 }}
                      inputProps={{ style: { textAlign: 'center' } }}
                    />
                    <TextField
                      label="Top"
                      type="number"
                      size="small"
                      value={printOffsets[key].top}
                      onChange={(e) => handleOffsetChange(key, 'top', e.target.value)}
                      sx={{ width: 80 }}
                      inputProps={{ style: { textAlign: 'center' } }}
                    />
                  </Box>
                ))}
              </Box>
              <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveOffsets}
                  disabled={savingOffsets}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Save Offsets
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveOffsets}
                  disabled={savingOffsets}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Remove Offsets
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Card>
      ) : (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddOffsets}
          sx={{ mb: 4, borderRadius: 2, textTransform: 'none' }}
        >
          Add Print Offsets
        </Button>
      )}

      {/* Bulk Activation Dialog */}
      <Dialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 600 }}>Bulk Activate Templates</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a version for each variant to publish atomically.
          </Typography>

          {publishError && <Alert severity="error" sx={{ mb: 2 }}>{publishError}</Alert>}

          <Stack spacing={2.5}>
            {UI_VARIANTS.map((uiVariant) => (
              <FormControl key={uiVariant} fullWidth size="small">
                <InputLabel>{UI_VARIANT_LABELS[uiVariant]}</InputLabel>
                <Select
                  value={selectedVersions[uiVariant] ?? ''}
                  onChange={(e) => handleVersionSelect(uiVariant, e.target.value as number)}
                  label={UI_VARIANT_LABELS[uiVariant]}
                >
                  {variantVersions[uiVariant].length === 0 ? (
                    <MenuItem value="" disabled>No versions available</MenuItem>
                  ) : (
                    variantVersions[uiVariant]
                      .filter(v => v.status === 'draft' || v.status === 'published')
                      .map((version) => (
                        <MenuItem key={version.version} value={version.version}>
                          v{version.version} — {version.status} ({formatDate(version.createdAt)})
                        </MenuItem>
                      ))
                  )}
                </Select>
              </FormControl>
            ))}
          </Stack>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              Summary
            </Typography>
            {UI_VARIANTS.map((uiVariant) => (
              <Typography key={uiVariant} variant="body2" sx={{ lineHeight: 1.8 }}>
                {UI_VARIANT_LABELS[uiVariant]}: {selectedVersions[uiVariant] !== null ? `v${selectedVersions[uiVariant]}` : '—'}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleCloseBulkDialog} disabled={publishing} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkPublish}
            variant="contained"
            disabled={publishing}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {publishing ? <CircularProgress size={20} /> : 'Publish All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TariffDetail;
